import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useState, useEffect } from 'react';
import { Typography, Box, Paper, IconButton, Button, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { brand } from './themes/main_theme.js';
import { useSwipeable } from 'react-swipeable';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useGalleryMedia, resolveOriginalUrl, GalleryItem } from '../data/cms.ts';

// Utility to shuffle array
function shuffleArray(array: any[]) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const PhotoCarousel = () => {
    const [media, setMedia] = useState<GalleryItem[]>([]);
    const [index, setIndex] = useState(0);

    const isMobile = useMediaQuery('(max-width:600px)');
    const { items: galleryMedia } = useGalleryMedia();
    const navigate = useNavigate();

    // Shuffle once the media has loaded. Resetting the index keeps it in range
    // if the fetched set is smaller than whatever was shown before.
    useEffect(() => {
      setMedia(shuffleArray(galleryMedia));
      setIndex(0);
    }, [galleryMedia]);

    // Slides show the full-size original, resolved as each one is reached.
    const [currentUrl, setCurrentUrl] = useState<string | null>(null);
    useEffect(() => {
      const item = media[index];
      if (!item) return;
      let cancelled = false;
      setCurrentUrl(null);
      resolveOriginalUrl(item).then(url => { if (!cancelled) setCurrentUrl(url); });
      return () => { cancelled = true; };
    }, [media, index]);

    const handlePrev = () => setIndex(i => (i === 0 ? media.length - 1 : i - 1));
    const handleNext = () => setIndex(i => (i === media.length - 1 ? 0 : i + 1));

    const swipeHandlers = useSwipeable({
      onSwipedLeft: handleNext,
      onSwipedRight: handlePrev,
      trackMouse: true,
    });
    if (!media.length) return null;

  const current = media[index];
  return (
    // 100vw includes the scrollbar, which pushed these pages 16px wider than
    // the viewport and produced a horizontal scroll on phones. 100% does not.
    <Paper elevation={0} sx={{ mt: 3, mb: 4, borderRadius: 5, bgcolor: 'background.paper', px: 2, pb: 3,
        maxWidth: 1600, width: '100%',
        mx: 'auto', overflow: 'hidden', border: `1px solid ${brand.hairline}` }}>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="primary">
            SALM Gallery
            </Typography>
            <Typography color="text.secondary" fontSize={16}>
            {index + 1} / {media.length}
            </Typography>
        </Box>
        <Box {...swipeHandlers} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', 
          touchAction: 'pan-y', cursor: isMobile ? 'grab' : 'default' }}>
            <IconButton onClick={handlePrev}><ArrowBackIosNewIcon /></IconButton>
            <Box sx={{ mx: 2, borderRadius: 4, overflow: 'hidden', boxShadow: 3, bgcolor: 'rgba(58,40,32,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '100%', maxWidth: isMobile ? 340 : 900, height: isMobile ? 220 : 460,}}>
            {current.type === 'image' ? (
                <img
                src={currentUrl ?? current.thumbUrl}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                />
            ) : (
                <video
                src={currentUrl ?? current.thumbUrl}
                controls
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4, background: '#222' }}
                />
            )}
            </Box>
            <IconButton onClick={handleNext}><ArrowForwardIosIcon /></IconButton>
        </Box>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button onClick={() => navigate('/gallery')} sx={{ color: 'secondary.main' }}>
              View all {media.length} photos
            </Button>
        </Box>
    </Paper>
  );
};

export default PhotoCarousel;